import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { locationPriceSuffix } from '../utils/listingPrice';
import './ListingCard.css';

const LABELS_TYPE = {
  'location': 'Location',
  'vente-maison': 'Vente',
  'terrain': 'Terrain',
};

const LABELS_STATUT = {
  en_attente: 'En attente',
  publie: 'Disponible',
  loue: 'Loué',
  vendu: 'Vendu',
  rejete: 'Rejeté',
};

const formaterPrix = (prix) =>
  new Intl.NumberFormat('fr-GN', { maximumFractionDigits: 0 }).format(prix) + ' GNF';

const tempsDepuis = (date) => {
  const jours = Math.floor((Date.now() - new Date(date)) / 86400000);
  if (jours === 0) return "Aujourd'hui";
  if (jours === 1) return 'Hier';
  return `Il y a ${jours}j`;
};

export default function ListingCard({ annonce }) {
  const { user, favorisIds, toggleFavori } = useAuth();
  const estFavori = favorisIds?.includes(annonce._id);
  const photo = annonce.photos?.[0]?.url;
  const paiementEchelonne = annonce.modePaiement && annonce.modePaiement !== 'comptant';
  const mensualite = paiementEchelonne && annonce.dureeMois
    ? Math.ceil((Number(annonce.prix) + Number(annonce.fraisFinancement || 0) - Number(annonce.apportInitial || 0)) / Number(annonce.dureeMois))
    : 0;

  const handleFavori = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.info('Connectez-vous pour sauvegarder des annonces');
      return;
    }
    const ajoute = await toggleFavori(annonce._id);
    if (ajoute) toast.success('Annonce ajoutée aux favoris');
    else toast.info('Annonce retirée des favoris');
  };

  return (
    <Link to={`/annonce/${annonce._id}`} className="listing-card">
      <div className="listing-card-img">
        {photo ? (
          <img src={photo} alt={annonce.titre} loading="lazy" decoding="async" sizes="(max-width: 640px) 100vw, 33vw" />
        ) : (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', color:'#cbd5e0' }}>
            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
        )}

        <div className="listing-card-badges">
          <span className={`listing-type listing-type-${annonce.type}`}>
            {LABELS_TYPE[annonce.type]}
          </span>
          {annonce.statut && (
            <span className={`listing-status-badge listing-status-${annonce.statut}`}>
              {LABELS_STATUT[annonce.statut] || annonce.statut}
            </span>
          )}
          {annonce.verifie && (
            <span className="listing-verifie-badge" title="Annonce vérifiée par Démarcheur">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
              Vérifié
            </span>
          )}
          {paiementEchelonne && <span className="listing-financement-badge">Paiement mensuel</span>}
        </div>

        <button
          className={`listing-card-fav${estFavori ? ' favori-actif' : ''}`}
          onClick={handleFavori}
          title={estFavori ? 'Retirer des favoris' : 'Sauvegarder'}
          aria-label={estFavori ? 'Retirer des favoris' : 'Sauvegarder'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill={estFavori ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
        </button>

        {(annonce.statut === 'loue' || annonce.statut === 'vendu') && (
          <div className="listing-overlay">
            {annonce.statut === 'loue' ? 'LOUÉ' : 'VENDU'}
          </div>
        )}
      </div>

      <div className="listing-card-body">
        <div className="listing-prix">
          {formaterPrix(annonce.prix)}
          {annonce.type === 'location' && <span className="listing-prix-suffix">{locationPriceSuffix(annonce)}</span>}
          {annonce.negotiable && <span className="listing-nego">Négociable</span>}
        </div>
        {paiementEchelonne && mensualite > 0 && (
          <div className="listing-mensualite">Dès {formaterPrix(mensualite)} <span>/mois</span></div>
        )}

        <h3 className="listing-titre">{annonce.titre}</h3>

        <p className="listing-localisation">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{flexShrink:0}}>
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          {annonce.quartier}, {annonce.ville}
        </p>

        <div className="listing-divider" />

        <div className="listing-card-footer">
          <span>{annonce.proprietaire?.nom || 'Propriétaire'}</span>
          <div className="card-footer-right">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`${annonce.titre} — ${annonce.quartier}, ${annonce.ville}\nPrix : ${formaterPrix(annonce.prix)}${annonce.type === 'location' ? ' ' + locationPriceSuffix(annonce) : ''}\nVoir sur Démarcheur : https://demarcheur.org/annonce/${annonce._id}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="card-wa-btn"
              onClick={e => e.stopPropagation()}
              title="Partager sur WhatsApp"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.138.564 4.141 1.543 5.873L.053 23.927l6.218-1.469A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.795 9.795 0 0 1-5.004-1.373l-.36-.213-3.69.872.936-3.581-.234-.368A9.78 9.78 0 0 1 2.182 12c0-5.42 4.398-9.818 9.818-9.818 5.42 0 9.818 4.398 9.818 9.818 0 5.42-4.398 9.818-9.818 9.818z"/>
              </svg>
              Partager
            </a>
            <span>{tempsDepuis(annonce.createdAt)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
